import * as React from "react";
import { observer } from "mobx-react-lite";
import { Tooltip, Skeleton, Row, Col, Collapse, Button } from "antd";
import { TaskViewVisualStore } from "../stores";
import { useNavigate, useParams } from "react-router-dom";
import { EntityTypeEnum, MetadataTypeEnum } from "../types";
import MetadataEditor from "./MetadataEditor";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Utils } from "../../common/misc/Utils";
type Props = {
    store: TaskViewVisualStore;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getExpandIcon: (panelProps: any) => JSX.Element;
};

const metadataTypeLabels: Record<MetadataTypeEnum, string> = {
    [MetadataTypeEnum.Submission]: "Submission",
    [MetadataTypeEnum.Quote]: "Quote",
    [MetadataTypeEnum.Renewal]: "Renewal",
    [MetadataTypeEnum.Binder]: "Binder",
    [MetadataTypeEnum.Endorsement]: "Endorsement",
    [MetadataTypeEnum.Policy]: "Policy",
    [MetadataTypeEnum.Certificate]: "Certificate",
    [MetadataTypeEnum.NewClaim]: "New Claim",
    [MetadataTypeEnum.ClaimUpdate]: "Claim Update",
    [MetadataTypeEnum.FinancialClaimUpdate]: "Financial Claim Update",
    [MetadataTypeEnum.ClaimClosure]: "Claim Closure",
    [MetadataTypeEnum.Other]: "Other",
};

const nestedMetadataSectionClassName =
    "alpha-task-section-collapse borderless no-padding metadata-nested-section";

const MetadataSection: React.FC<Props> = ({ store, getExpandIcon }) => {
    const navigateTo = useNavigate();
    const { taskId } = useParams();
    const { metadata } = store.taskPreview!;
    const baseMetadata = metadata?.core;
    const contractsMetadata = metadata?.domain?.contracts;
    const claimsMetadata = metadata?.domain?.claims;
    const worksheetsMetadata = metadata?.domain?.worksheets;
    const eMessagesMetadata = metadata?.domain?.eMessages;

    const copyMetadataValue = (
        e: React.MouseEvent<HTMLElement>,
        value: React.ReactNode,
    ) => {
        e.stopPropagation();
        Utils.copyTextToClipboard(String(value ?? ""));
    };

    const formatMetadataType = (value?: MetadataTypeEnum) =>
        value ? metadataTypeLabels[value] : value;

    const MetadataValue: React.FC<{ value: React.ReactNode }> = ({ value }) => (
        <div className="metadata-value-with-copy">
            <span className="metadata-value-text">{value}</span>
            {value !== undefined && value !== null && value !== "" && (
                <Tooltip title="Copy value">
                    <Button
                        type="link"
                        size="small"
                        className="metadata-copy-button"
                        onClick={(e) => copyMetadataValue(e, value)}
                        aria-label="Copy metadata value"
                    >
                        <CopyOutlined />
                    </Button>
                </Tooltip>
            )}
        </div>
    );

    const MetadataRow: React.FC<{
        rowKey: string;
        label: string;
        value: React.ReactNode;
    }> = ({ rowKey, label, value }) => (
        <Row className="inputs-container input-row" key={rowKey}>
            <Col span={10}>
                <span className="input-label">{label}</span>
            </Col>
            <Col span={14}>
                <MetadataValue value={value} />
            </Col>
        </Row>
    );

    const handleMetadataChangesClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (taskId === store.taskPreview!.id) {
            store.setIsMetadataChangesDialogVisible(true);
            return;
        }
        store.setPageIsLoading(true);
        navigateTo(
            `/tasks/${store.taskPreview!.id}?show-metadata-changes=true`,
        );
    };

    const SystemDetailsSection = (
        <Collapse
            bordered={false}
            expandIcon={getExpandIcon}
            className={nestedMetadataSectionClassName}
            items={[
                {
                    key: "system-details",
                    label: (
                        <div className="metadata-title-container">
                            <div className="title-with-icon">
                                <div className="flex">System details</div>
                                <Tooltip title="Auto-generated by system">
                                    <InfoCircleOutlined
                                        style={{ fontSize: 12 }}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    ),
                    children: (
                        <>
                            <Row
                                className="inputs-container input-row"
                                key={"source-channel"}
                            >
                                <Col span={10}>
                                    <span className="input-label">Source</span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={baseMetadata?.sourceSystem}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"channel"}
                            >
                                <Col span={10}>
                                    <span className="input-label">Channel</span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={baseMetadata?.channel}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"enrichment-updated-at"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Enrichment updated at
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={
                                            baseMetadata?.enrichmentUpdatedAt
                                                ? Utils.formatDateStringShort(
                                                      baseMetadata.enrichmentUpdatedAt,
                                                  )
                                                : ""
                                        }
                                    />
                                </Col>
                            </Row>
                        </>
                    ),
                },
            ]}
        />
    );

    const ContractsSection = (
        <Collapse
            bordered={false}
            expandIcon={getExpandIcon}
            className={nestedMetadataSectionClassName}
            items={[
                {
                    key: "contract-fields",
                    label: (
                        <div className="metadata-title-container">
                            <div className="title-with-icon">
                                <div className="flex">Contract fields</div>
                                <Tooltip title="Standard fields for this entity type">
                                    <InfoCircleOutlined
                                        style={{ fontSize: 12 }}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    ),
                    children: (
                        <>
                            <Row
                                className="inputs-container input-row"
                                key={"business-id"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Business ID
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={contractsMetadata?.businessId}
                                    />
                                </Col>
                            </Row>
                            <MetadataRow
                                rowKey="umr"
                                label="UMR"
                                value={contractsMetadata?.umr}
                            />
                            <MetadataRow
                                rowKey="contract-reference"
                                label="Contract reference"
                                value={contractsMetadata?.contractReference}
                            />
                            <Row
                                className="inputs-container input-row"
                                key={"title"}
                            >
                                <Col span={10}>
                                    <span className="input-label">Title</span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={contractsMetadata?.title}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"uw-year"}
                            >
                                <Col span={10}>
                                    <span className="input-label">UW year</span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={contractsMetadata?.uwYear}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"participation"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Participation
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={contractsMetadata?.participation}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"main-class"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Main class
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={contractsMetadata?.mainClass}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"currency"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Currency
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={contractsMetadata?.currency}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"cedent"}
                            >
                                <Col span={10}>
                                    <span className="input-label">Cedent</span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={contractsMetadata?.cedent}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"broker"}
                            >
                                <Col span={10}>
                                    <span className="input-label">Broker</span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={contractsMetadata?.broker}
                                    />
                                </Col>
                            </Row>
                        </>
                    ),
                },
            ]}
        />
    );

    const ClaimsSection = (
        <Collapse
            bordered={false}
            expandIcon={getExpandIcon}
            className={nestedMetadataSectionClassName}
            items={[
                {
                    key: "claim-fields",
                    label: (
                        <div className="metadata-title-container">
                            <div className="title-with-icon">
                                <div className="flex">Claim fields</div>
                                <Tooltip title="Standard fields for this entity type">
                                    <InfoCircleOutlined
                                        style={{ fontSize: 12 }}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    ),
                    children: (
                        <>
                            <Row
                                className="inputs-container input-row"
                                key={"claimId"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Claim ID
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={claimsMetadata?.claimId}
                                    />
                                </Col>
                            </Row>
                            <MetadataRow
                                rowKey="ucr"
                                label="UCR"
                                value={claimsMetadata?.ucr}
                            />
                            <Row
                                className="inputs-container input-row"
                                key={"claimReference"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Claim reference
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={claimsMetadata?.claimReference}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"claimName"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Claim name
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={claimsMetadata?.claimName}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"lossDate"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Loss date
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={
                                            claimsMetadata?.lossDate
                                                ? Utils.formatDateStringShort(
                                                      claimsMetadata.lossDate,
                                                      true,
                                                      true,
                                                      false,
                                                      true,
                                                  )
                                                : ""
                                        }
                                    />
                                </Col>
                            </Row>
                            <MetadataRow
                                rowKey="cedent"
                                label="Cedent"
                                value={claimsMetadata?.cedent}
                            />
                            <MetadataRow
                                rowKey="broker"
                                label="Broker"
                                value={claimsMetadata?.broker}
                            />
                            <MetadataRow
                                rowKey="insured"
                                label="Insured"
                                value={claimsMetadata?.insured}
                            />
                            <MetadataRow
                                rowKey="umr"
                                label="UMR"
                                value={claimsMetadata?.umr}
                            />
                            <MetadataRow
                                rowKey="insured-period"
                                label="Insured period"
                                value={claimsMetadata?.insuredPeriod}
                            />
                            <MetadataRow
                                rowKey="contract-type"
                                label="Contract type"
                                value={claimsMetadata?.contractType}
                            />
                            <MetadataRow
                                rowKey="currency"
                                label="Currency"
                                value={claimsMetadata?.currency}
                            />
                            <MetadataRow
                                rowKey="amount"
                                label="Amount"
                                value={Utils.formatNumberWithSeparators(
                                    claimsMetadata?.amount,
                                )}
                            />
                            <MetadataRow
                                rowKey="headline-loss"
                                label="Headline loss"
                                value={
                                    claimsMetadata?.isHeadlineLoss == null
                                        ? undefined
                                        : String(claimsMetadata.isHeadlineLoss)
                                }
                            />
                        </>
                    ),
                },
            ]}
        />
    );

    const WorksheetSection = (
        <Collapse
            bordered={false}
            expandIcon={getExpandIcon}
            className={nestedMetadataSectionClassName}
            items={[
                {
                    key: "claim-fields",
                    label: (
                        <div className="metadata-title-container">
                            <div className="title-with-icon">
                                <div className="flex">Worksheet fields</div>
                                <Tooltip title="Standard fields for this entity type">
                                    <InfoCircleOutlined
                                        style={{ fontSize: 12 }}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    ),
                    children: (
                        <>
                            <Row
                                className="inputs-container input-row"
                                key={"worksheetId"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Worksheet ID
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={worksheetsMetadata?.worksheetId}
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"worksheetType"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Worksheet type
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={
                                            worksheetsMetadata?.worksheetType
                                        }
                                    />
                                </Col>
                            </Row>
                            <Row
                                className="inputs-container input-row"
                                key={"accountingPeriod"}
                            >
                                <Col span={10}>
                                    <span className="input-label">
                                        Accounting period
                                    </span>
                                </Col>
                                <Col span={14}>
                                    <MetadataValue
                                        value={
                                            worksheetsMetadata?.accountingPeriod
                                        }
                                    />
                                </Col>
                            </Row>
                        </>
                    ),
                },
            ]}
        />
    );

    const EMessagesSection = (
        <Collapse
            bordered={false}
            expandIcon={getExpandIcon}
            className={nestedMetadataSectionClassName}
            items={[
                {
                    key: "eMessage-fields",
                    label: (
                        <div className="metadata-title-container">
                            <div className="title-with-icon">
                                <div className="flex">eMessage fields</div>
                                <Tooltip title="Standard fields for this entity type">
                                    <InfoCircleOutlined
                                        style={{ fontSize: 12 }}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    ),
                    children: (
                        <Row
                            className="inputs-container input-row"
                            key={"uuid"}
                        >
                            <Col span={10}>
                                <span className="input-label">UUID</span>
                            </Col>
                            <Col span={14}>
                                <MetadataValue
                                    value={eMessagesMetadata?.uuid}
                                />
                            </Col>
                        </Row>
                    ),
                },
            ]}
        />
    );

    const ProjectFieldsSection = (
        <Collapse
            bordered={false}
            expandIcon={getExpandIcon}
            className={nestedMetadataSectionClassName}
            items={[
                {
                    key: "project-fields",
                    label: (
                        <div className="metadata-title-container">
                            <div className="title-with-icon">
                                <div className="flex">Project fields</div>
                                <Tooltip title="Custom project-specific fields">
                                    <InfoCircleOutlined
                                        style={{ fontSize: 12 }}
                                    />
                                </Tooltip>
                            </div>
                            {store.taskPreview!.metadataAudits.length > 0 && (
                                <Tooltip
                                    title={`Updated by ${store.getLatestMetadataAuditUpdateUser()} on ${store.getLatestMetadataAuditUpdateTime()}`}
                                >
                                    <div
                                        className="changes"
                                        onClick={handleMetadataChangesClick}
                                    >
                                        {store.metadataAudits.length === 0
                                            ? ""
                                            : `${store.metadataAudits.length} change(s)`}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    ),
                    children: store.metadataDefinitionsLoading ? (
                        <Skeleton />
                    ) : (
                        <div>
                            {store.metadataDefinitions
                                .filter((m) =>
                                    store.hasAccessToMetadata(m.taskTypes),
                                )
                                .slice()
                                .sort((a, b) => a.position - b.position)
                                .map((x) => (
                                    <Row
                                        className="inputs-container input-row"
                                        key={x.name}
                                    >
                                        <Col span={10}>
                                            <span className="input-label">
                                                {x.title ?? x.name}
                                            </span>
                                        </Col>
                                        <Col span={14}>
                                            <MetadataEditor
                                                store={store}
                                                definition={x}
                                                task={store.taskPreview!}
                                            />
                                        </Col>
                                    </Row>
                                ))}
                            {!store.metadataDefinitions.length && (
                                <div className="empty-message">
                                    There are no metadata yet.
                                </div>
                            )}
                        </div>
                    ),
                },
            ]}
        />
    );

    const metadataCollapsePanel = () => {
        return [
            {
                key: "metadata",
                label: (
                    <div className="metadata-title-container">
                        <div className="title-with-icon">
                            <div className="flex">Context</div>
                            <Tooltip title="Defines task context">
                                <InfoCircleOutlined style={{ fontSize: 12 }} />
                            </Tooltip>
                        </div>
                    </div>
                ),
                children: store.metadataDefinitionsLoading ? (
                    <Skeleton />
                ) : (
                    <div className="dialog-section" style={{ marginBottom: 0 }}>
                        <Row
                            className="inputs-container input-row"
                            key={"Submission ID"}
                        >
                            <Col span={10}>
                                <span className="input-label">
                                    Submission ID
                                </span>
                            </Col>
                            <Col span={14}>
                                <MetadataValue
                                    value={baseMetadata?.submissionId}
                                />
                            </Col>
                        </Row>
                        <Row
                            className="inputs-container input-row"
                            key={"entity"}
                        >
                            <Col span={10}>
                                <span className="input-label">Entity</span>
                            </Col>
                            <Col span={14}>
                                <MetadataValue
                                    value={baseMetadata?.entityType}
                                />
                            </Col>
                        </Row>
                        <Row
                            className="inputs-container input-row"
                            key={"type"}
                        >
                            <Col span={10}>
                                <span className="input-label">Type</span>
                            </Col>
                            <Col span={14}>
                                <MetadataValue
                                    value={formatMetadataType(
                                        baseMetadata?.type,
                                    )}
                                />
                            </Col>
                        </Row>
                        <Row
                            className="inputs-container input-row"
                            key={"confidence"}
                        >
                            <Col span={10}>
                                <span className="input-label">Confidence</span>
                            </Col>
                            <Col span={14}>
                                <MetadataValue
                                    value={baseMetadata?.confidence}
                                />
                            </Col>
                        </Row>
                        {SystemDetailsSection}
                        {metadata?.core?.entityType ===
                            EntityTypeEnum.Contract && ContractsSection}
                        {metadata?.core?.entityType === EntityTypeEnum.Claim &&
                            ClaimsSection}
                        {metadata?.core?.entityType ===
                            EntityTypeEnum.Worksheet && WorksheetSection}
                        {metadata?.core?.entityType ===
                            EntityTypeEnum.EMessage && EMessagesSection}
                        {ProjectFieldsSection}
                    </div>
                ),
            },
        ];
    };

    return (
        <div data-id-type="collapse-container" data-id-name="Metadata">
            <Collapse
                bordered={false}
                expandIcon={getExpandIcon}
                activeKey={
                    store.activeSectionKeys.includes("metadata")
                        ? "metadata"
                        : undefined
                }
                onChange={(keys: string[]) =>
                    store.setSectionActiveKey("metadata", !!keys[0])
                }
                className="alpha-task-section-collapse"
                items={metadataCollapsePanel()}
            />
        </div>
    );
};
export default observer(MetadataSection);
